import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/api";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Inbox, User, Flag, CalendarClock, Pencil, Trash2 } from "lucide-react";
import CreateJobDialog from "@/components/CreateJobDialog";
import SearchBar from "@/components/SearchBar";
import { Highlight } from "@/lib/highlight";

// The Pipeline is a managers-only bucket of jobs that don't yet have a
// firm date. Adding a date on any card promotes it onto the schedule
// automatically (backend `is_pipeline` flag flips to false).
export default function PipelinePage() {
    const [jobs, setJobs] = useState([]);
    const [operatives, setOperatives] = useState([]);
    const [clientsById, setClientsById] = useState({});
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingJob, setEditingJob] = useState(null);
    const [query, setQuery] = useState("");
    const navigate = useNavigate();

    const load = async () => {
        try {
            const [j, u, c] = await Promise.all([
                api.get("/jobs?scope=pipeline"),
                api.get("/users"),
                api.get("/clients").catch(() => ({ data: [] })),
            ]);
            setJobs(j.data);
            setOperatives(u.data);
            setClientsById(Object.fromEntries(c.data.map(x => [x.id, x])));
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const opById = useMemo(
        () => Object.fromEntries(operatives.map(o => [o.id, o])),
        [operatives],
    );

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return jobs;
        return jobs.filter(j => {
            const c = clientsById[j.client_id] || {};
            const hay = [
                j.title, j.description, j.notes, j.client_name, j.assigned_to_name, j.address,
                (j.checklist || []).join(" "),
                c.address, c.contact_name, c.contact_phone, c.contact_email,
            ].filter(Boolean).join(" ").toLowerCase();
            return hay.includes(q);
        });
    }, [jobs, query, clientsById]);

    const removeJob = async (job) => {
        if (!window.confirm(`Remove "${job.title}" from the Pipeline? This cannot be undone.`)) return;
        try {
            await api.delete(`/jobs/${job.id}`);
            setJobs(prev => prev.filter(j => j.id !== job.id));
            toast.success("Removed from Pipeline");
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    return (
        <div className="space-y-6" data-testid="pipeline-page">
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                    <span className="text-xs uppercase tracking-[0.22em] text-[var(--terracotta)] font-semibold">Pipeline</span>
                    <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-1">Upcoming — no firm date yet</h1>
                    <p className="text-[var(--text-secondary)] mt-1 text-sm max-w-2xl">
                        Capture leads and enquiries here while you're still nailing down a time. Adding a date on any card promotes it onto the Schedule automatically. Only managers see this list.
                    </p>
                </div>
                <button
                    data-testid="pipeline-add-btn"
                    onClick={() => { setEditingJob(null); setDialogOpen(true); }}
                    className="btn-primary inline-flex items-center gap-2">
                    <Plus size={14} /> Add to Pipeline
                </button>
            </div>

            <SearchBar
                value={query}
                onChange={setQuery}
                placeholder="Search Pipeline — client, address, notes…"
                resultLabel={query.trim() ? `${filtered.length} match${filtered.length === 1 ? "" : "es"}` : null}
            />

            {loading ? (
                <div className="text-sm text-[var(--text-muted)]">Loading…</div>
            ) : filtered.length === 0 ? (
                <div
                    data-testid="pipeline-empty"
                    className="card-flat p-10 text-center border-dashed">
                    <Inbox size={28} className="mx-auto text-[var(--text-muted)]" />
                    <div className="mt-3 font-semibold text-[var(--text-primary)]">
                        {jobs.length === 0 ? "Nothing in the Pipeline yet" : "No matches"}
                    </div>
                    <div className="text-sm text-[var(--text-muted)] mt-1 max-w-md mx-auto">
                        Add a job here whenever a customer flags future work without picking a date yet. Fill in the date on the card when you know it and it'll jump straight into the Schedule.
                    </div>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="pipeline-grid">
                    {filtered.map(job => {
                        const client = clientsById[job.client_id];
                        const clientColor = client?.color;
                        const assignee = job.assigned_to ? opById[job.assigned_to] : null;
                        return (
                            <div
                                key={job.id}
                                data-testid={`pipeline-card-${job.id}`}
                                className="card-flat p-4 sm:p-5 flex flex-col gap-3"
                                style={{
                                    borderLeft: `4px solid ${clientColor || "var(--terracotta)"}`,
                                }}>
                                <div className="min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="pill status-scheduled" style={{ background: "#FDF3EB", color: "var(--terracotta)" }}>
                                            <Inbox size={12} /> Pipeline
                                        </span>
                                        {job.priority === "high" && (
                                            <span className="pill priority-high"><Flag size={12} /> High</span>
                                        )}
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => navigate(`/jobs/${job.id}`)}
                                        className="mt-2 text-left w-full">
                                        <div className="font-display font-semibold text-lg leading-tight">
                                            <Highlight text={job.title} query={query} />
                                        </div>
                                        <div className="text-sm text-[var(--text-secondary)] mt-0.5">
                                            <Highlight text={job.client_name || "—"} query={query} />
                                        </div>
                                        {job.address && (
                                            <div className="text-xs text-[var(--text-muted)] mt-0.5 truncate">
                                                <Highlight text={job.address} query={query} />
                                            </div>
                                        )}
                                    </button>
                                </div>

                                <div className="text-xs text-[var(--text-secondary)] flex items-center gap-1.5">
                                    <User size={12} className="shrink-0" />
                                    {assignee ? (
                                        <span className="inline-flex items-center gap-1.5">
                                            {assignee.color && (
                                                <span className="h-2 w-2 rounded-full shrink-0" style={{ background: assignee.color }} />
                                            )}
                                            {assignee.name}
                                        </span>
                                    ) : (
                                        <span className="italic text-[var(--text-muted)]">Not assigned yet</span>
                                    )}
                                </div>

                                {job.notes && (
                                    <div className="text-xs text-[var(--text-secondary)] line-clamp-3 whitespace-pre-wrap">
                                        {job.notes}
                                    </div>
                                )}

                                <div className="mt-auto pt-2 border-t border-[var(--border-soft)] flex items-center gap-2">
                                    <button
                                        data-testid={`pipeline-set-date-${job.id}`}
                                        onClick={() => { setEditingJob(job); setDialogOpen(true); }}
                                        className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-full bg-[var(--pine)] text-white text-xs font-semibold py-1.5 px-3 hover:opacity-90 transition-opacity">
                                        <CalendarClock size={12} /> Set date & book in
                                    </button>
                                    <button
                                        data-testid={`pipeline-edit-${job.id}`}
                                        onClick={() => { setEditingJob(job); setDialogOpen(true); }}
                                        title="Edit details"
                                        aria-label="Edit details"
                                        className="p-1.5 rounded-lg text-[var(--text-muted)] hover:text-[var(--pine)] hover:bg-[var(--pine-light)]">
                                        <Pencil size={13} />
                                    </button>
                                    <button
                                        data-testid={`pipeline-delete-${job.id}`}
                                        onClick={() => removeJob(job)}
                                        title="Remove"
                                        aria-label="Remove from Pipeline"
                                        className="p-1.5 rounded-lg text-[var(--text-muted)] hover:text-red-600 hover:bg-red-50">
                                        <Trash2 size={13} />
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            <CreateJobDialog
                open={dialogOpen}
                onClose={() => { setDialogOpen(false); setEditingJob(null); }}
                onCreated={(created) => {
                    if (created?.is_pipeline) {
                        setJobs(prev => [created, ...prev]);
                    }
                    load();
                }}
                onUpdated={(updated) => {
                    // If the updated job is no longer a pipeline entry (it got a
                    // date → graduated to the schedule) drop it from this list.
                    if (updated && !updated.is_pipeline) {
                        setJobs(prev => prev.filter(j => j.id !== updated.id));
                        toast.success("Booked into the schedule");
                    } else {
                        setJobs(prev => prev.map(j => j.id === updated.id ? updated : j));
                    }
                }}
                operatives={operatives}
                job={editingJob}
                pipelineMode={!editingJob}
            />
        </div>
    );
}
